/* eslint-disable max-lines */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import Button from '@components/atoms/ui-kit/button';
import Input from '@components/atoms/ui-kit/input';
import Switch from '@components/atoms/ui-kit/switch';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';
import Modal from '@components/atoms/modals/modal';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';
import {
  useGetSystemSettings,
  useUpdateSystemSetting,
  useCreateSystemSetting,
  useDeleteSystemSetting
} from '@utils/api/back-office/settings';
import {
  SystemSetting,
  parseSettingValue,
  stringifySettingValue
} from '@utils/types/back-office/settings';
import {
  Sliders,
  Settings,
  Search,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function DefaultsSettings() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState<SystemSetting | null>(
    null
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDefault, setNewDefault] = useState<{
    key: string;
    value: string;
    value_type: 'string' | 'integer' | 'boolean' | 'json';
    description: string;
  }>({
    key: '',
    value: '',
    value_type: 'string',
    description: ''
  });

  const { data, isLoading } = useGetSystemSettings({ category: 'default' });
  const { mutate: updateSetting, isLoading: isUpdating } =
    useUpdateSystemSetting();
  const { mutate: createSetting, isLoading: isCreating } =
    useCreateSystemSetting();
  const { mutate: deleteSetting } = useDeleteSystemSetting();

  const defaults = data?.result || [];

  // Filter defaults by search query
  const filteredDefaults = defaults.filter(setting => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      setting.key.toLowerCase().includes(query) ||
      (setting.description?.toLowerCase() || '').includes(query)
    );
  });

  const handleEdit = (setting: SystemSetting) => {
    setEditingId(setting.id);
    setEditValue(setting.value);
    setEditDescription(setting.description || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setEditDescription('');
  };

  const handleSave = (setting: SystemSetting) => {
    updateSetting(
      {
        id: setting.id,
        payload: {
          value: editValue,
          value_type: setting.value_type,
          is_active: setting.is_active,
          description: editDescription || undefined
        }
      },
      {
        onSuccess: () => {
          toast.success(t('default-updated'));
          handleCancelEdit();
        },
        onError: () => {
          toast.error(t('failed-to-update-default'));
        }
      }
    );
  };

  const handleToggleActive = (setting: SystemSetting) => {
    updateSetting(
      {
        id: setting.id,
        payload: {
          value: setting.value,
          value_type: setting.value_type,
          is_active: !setting.is_active
        }
      },
      {
        onSuccess: () => {
          toast.success(
            setting.is_active
              ? t('default-deactivated')
              : t('default-activated')
          );
        },
        onError: () => {
          toast.error(t('failed-to-update-default'));
        }
      }
    );
  };

  const handleToggleBoolean = (setting: SystemSetting) => {
    const currentValue = parseSettingValue(setting);
    if (typeof currentValue !== 'boolean') return;

    updateSetting(
      {
        id: setting.id,
        payload: {
          value: stringifySettingValue(!currentValue),
          value_type: setting.value_type,
          is_active: setting.is_active
        }
      },
      {
        onSuccess: () => {
          toast.success(t('default-updated'));
        },
        onError: () => {
          toast.error(t('failed-to-update-default'));
        }
      }
    );
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    deleteSetting(confirmDelete.id, {
      onSuccess: () => {
        toast.success(t('default-deleted'));
        setConfirmDelete(null);
      },
      onError: () => {
        toast.error(t('failed-to-delete-default'));
      }
    });
  };

  const handleCreateDefault = () => {
    if (!newDefault.key.trim()) {
      toast.error(t('key-is-required'));
      return;
    }

    createSetting(
      {
        key: newDefault.key.trim(),
        value: newDefault.value,
        value_type: newDefault.value_type,
        category: 'default',
        description: newDefault.description || undefined
      },
      {
        onSuccess: () => {
          toast.success(t('default-created'));
          setShowAddModal(false);
          setNewDefault({
            key: '',
            value: '',
            value_type: 'string',
            description: ''
          });
        },
        onError: () => {
          toast.error(t('failed-to-create-default'));
        }
      }
    );
  };

  const formatDisplayValue = (setting: SystemSetting): string => {
    if (setting.value_type === 'json') {
      // For JSON, display the raw string value (already formatted)
      return setting.value;
    }
    return setting.value;
  };

  const renderSettingValue = (setting: SystemSetting) => {
    const isEditing = editingId === setting.id;
    const value = parseSettingValue(setting);

    if (isEditing) {
      if (setting.value_type === 'boolean') {
        return (
          <Switch
            checked={editValue === 'true'}
            onChange={e => setEditValue(e.target.checked ? 'true' : 'false')}
          />
        );
      }
      return (
        <Input
          type={setting.value_type === 'integer' ? 'number' : 'text'}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          size="small"
          className="w-40"
        />
      );
    }

    if (setting.value_type === 'boolean') {
      return (
        <Switch
          checked={value as boolean}
          onChange={() => handleToggleBoolean(setting)}
          disabled={!setting.is_active}
        />
      );
    }

    const displayValue = formatDisplayValue(setting);
    const isTruncated = displayValue.length > 30;

    return (
      <span
        className="text-sm font-mono px-2 py-1 rounded-2xl max-w-[200px] truncate inline-block"
        style={{
          backgroundColor: getColour('thirdLayer'),
          color: getColour('normalText')
        }}
        title={isTruncated ? displayValue : undefined}
      >
        {displayValue}
      </span>
    );
  };

  const renderSettingRow = (setting: SystemSetting) => {
    const isEditing = editingId === setting.id;

    return (
      <div
        key={setting.id}
        className="flex items-center justify-between p-3"
        style={{
          borderColor: getColour('border'),
          opacity: setting.is_active ? 1 : 0.6
        }}
      >
        <div className="flex-1 min-w-0 pr-4">
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-2xl bg-amber-400/10">
              <Sliders className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400" />
            </div>
            <p
              className="text-sm font-mono truncate"
              style={{ color: getColour('normalText') }}
              title={setting.key}
            >
              {setting.key}
            </p>
            {!setting.is_active && (
              <span className="text-xs px-1.5 py-0.5 rounded-2xl bg-zinc-600/10 text-zinc-600 dark:bg-white/5 dark:text-zinc-400">
                {t('inactive')}
              </span>
            )}
            <span
              className="text-xs px-1.5 py-0.5 rounded-2xl"
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('inactiveText')
              }}
            >
              {setting.value_type}
            </span>
          </div>
          {isEditing ? (
            <Input
              value={editDescription}
              onChange={e => setEditDescription(e.target.value)}
              placeholder={t('description')}
              size="small"
              className="mt-1 pl-7"
            />
          ) : (
            setting.description && (
              <p
                className="text-xs mt-0.5 truncate pl-7"
                style={{ color: getColour('inactiveText') }}
                title={setting.description}
              >
                {setting.description}
              </p>
            )
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 mt-6">
          {renderSettingValue(setting)}
          {isEditing ? (
            <>
              <Button
                label={t('cancel')}
                variant="text"
                size="small"
                onClick={handleCancelEdit}
              />
              <Button
                label={t('save')}
                variant="primary"
                size="small"
                icon={<Save className="h-3.5 w-3.5" />}
                onClick={() => handleSave(setting)}
                disabled={isUpdating}
              />
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleEdit(setting)}
                className="p-1.5 rounded-2xl hover:bg-black/5 dark:hover:bg-white/10"
                title={t('edit')}
              >
                <Edit
                  className="h-3 w-3"
                  style={{ color: getColour('inactiveText') }}
                />
              </button>
              <button
                type="button"
                onClick={() => handleToggleActive(setting)}
                className="p-1.5 rounded-2xl hover:bg-black/5 dark:hover:bg-white/10"
                title={setting.is_active ? t('deactivate') : t('activate')}
              >
                {setting.is_active ? (
                  <Check className="h-3 w-3 text-green-500 dark:text-green-400" />
                ) : (
                  <X className="h-3 w-3 text-zinc-500 dark:text-zinc-400" />
                )}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(setting)}
                className="p-1.5 rounded-2xl hover:bg-red-50 dark:hover:bg-red-950/30"
                title={t('delete')}
              >
                <Trash2 className="h-3 w-3 text-red-500 dark:text-red-400" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton variant="table" rows={5} />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3
            className="text-sm font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('system-defaults')}
          </h3>
          <p
            className="text-xs mt-0.5"
            style={{ color: getColour('inactiveText') }}
          >
            {t('configure-default-values-for-new-customers-and-processing')}
          </p>
        </div>
        <Button
          label={t('add-default')}
          variant="primary"
          size="small"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddModal(true)}
        />
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
          style={{ color: getColour('inactiveText') }}
        />
        <input
          type="text"
          placeholder={t('search-defaults')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm rounded-2xl border"
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border'),
            color: getColour('normalText')
          }}
        />
      </div>

      {/* Defaults list */}
      <div
        className="rounded-2xl overflow-hidden divide-y"
        style={{
          backgroundColor: getColour('secondLayer'),
          borderColor: getColour('border')
        }}
      >
        {filteredDefaults.map(setting => renderSettingRow(setting))}
      </div>

      {filteredDefaults.length === 0 && !isLoading && (
        <EmptyState
          icon={<Settings className="h-7 w-7" />}
          title={
            searchQuery
              ? t('no-defaults-match-search')
              : t('no-defaults-configured')
          }
        />
      )}

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title={t('delete-default')}
        caption={t('confirm-delete-default', { key: confirmDelete?.key || '' })}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        destructive
      />

      {/* Add default modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t('add-system-default')}
        size="small"
        footerActions={[
          <Button
            key="cancel"
            label={t('cancel')}
            variant="secondary"
            onClick={() => setShowAddModal(false)}
          />,
          <Button
            key="create"
            label={isCreating ? t('creating') : t('create')}
            onClick={handleCreateDefault}
            disabled={isCreating || !newDefault.key.trim()}
          />
        ]}
      >
        <div className="space-y-4">
          <Input
            label={t('key')}
            value={newDefault.key}
            onChange={e =>
              setNewDefault(prev => ({ ...prev, key: e.target.value }))
            }
            placeholder="customer_retention_days"
            required
          />
          <div>
            <label
              className="text-xs mb-1 block"
              style={{ color: getColour('inactiveText') }}
            >
              {t('value-type')}
            </label>
            <select
              value={newDefault.value_type}
              onChange={e => {
                const type = e.target.value as
                  | 'boolean'
                  | 'string'
                  | 'integer'
                  | 'json';
                setNewDefault(prev => ({
                  ...prev,
                  value_type: type,
                  value:
                    type === 'boolean' ? 'false' : type === 'integer' ? '0' : ''
                }));
              }}
              className="w-full px-3 py-2 text-sm rounded-2xl border"
              style={{
                backgroundColor: getColour('secondLayer'),
                borderColor: getColour('border'),
                color: getColour('normalText')
              }}
            >
              <option value="string">String</option>
              <option value="integer">Integer</option>
              <option value="boolean">Boolean</option>
              <option value="json">JSON</option>
            </select>
          </div>
          {newDefault.value_type === 'boolean' ? (
            <div className="flex items-center justify-between">
              <label style={{ color: getColour('normalText') }}>
                {t('value')}
              </label>
              <Switch
                checked={newDefault.value === 'true'}
                onChange={e =>
                  setNewDefault(prev => ({
                    ...prev,
                    value: e.target.checked ? 'true' : 'false'
                  }))
                }
              />
            </div>
          ) : (
            <Input
              label={t('value')}
              type={newDefault.value_type === 'integer' ? 'number' : 'text'}
              value={newDefault.value}
              onChange={e =>
                setNewDefault(prev => ({ ...prev, value: e.target.value }))
              }
              required
            />
          )}
          <Input
            label={t('description')}
            value={newDefault.description}
            onChange={e =>
              setNewDefault(prev => ({ ...prev, description: e.target.value }))
            }
            placeholder={t('optional-description')}
          />
        </div>
      </Modal>
    </div>
  );
}
